// Central project/case-study data used by ProjectsPage and the individual
// case-study detail pages. Keep this in sync with src/App.js routes.

const projects = [
  {
    slug: 'warehouse-consolidation',
    path: '/material-zone-analytics',
    title: 'Warehouse Consolidation Opportunity Model',
    tagline:
      'A scoring model that turns raw SAP WMS extracts into concrete consolidation and slotting recommendations across A/B/C/PLT storage zones.',
    featured: true,
    order: 1,
    cardDescription:
      'Data-driven consolidation, mismatch auditing, and zone-classification scoring built from anonymized SAP WMS extracts. Flagship project — fully interactive, with live tables and KPIs.',
    // This project keeps its own bespoke component (materialZoneAnalytics.js)
    // instead of rendering through the shared CaseStudy component.
    componentOverride: true,
    sections: {
      problem:
        'Replenishment and picking were drifting toward slower B/C/D/overflow zones, and nobody had a repeatable way to see which storage bins were misclassified or which handling units were candidates for consolidation. Decisions were being made on gut feel instead of the data already sitting in the warehouse management system.',
      dataSources:
        'Anonymized SAP WMS extracts: material-location reports (bins, batches, handling units, goods-receipt dates), a pick-area vs. physical-inventory audit export, and daily A-vs-non-A pick ratio pivots.',
      pipeline:
        'Python cleanup and audit scripts normalize bin, batch, and handling-unit fields across exports, flag pick-area/physical-inventory mismatches, and classify materials into A/B/C/PLT priority zones. A FIFO + consolidation scoring pass ranks handling units by goods-receipt date and flags multi-HU same-batch bins as consolidation candidates.',
      tools: ['Python', 'pandas', 'Excel', 'SAP WMS exports'],
      outcome:
        'Flagged roughly 5,692 bin+batch consolidation opportunities and about 500 mismatched bins during an A-zone audit, and tracked the A:Non-A pick ratio recovering from ~0.61 to 1.32 over the analysis window as consolidation and replenishment aligned.',
      improvements: [
        'Separate raw, curated, and reporting layers instead of re-deriving metrics inline from anonymized exports.',
        'Add freshness checks on the WMS extracts so stale data is caught before it reaches the scoring model.',
        'Add reconciliation/validation tests comparing mismatch and consolidation counts run-over-run.',
        'Push more of the cleanup/scoring logic into a warehouse-first transformation layer rather than ad hoc Python scripts.',
        'Version the pipeline configuration (zone rules, thresholds) instead of hardcoding them in scripts.',
        'Add observability/error handling around the extract-ingest step so failures surface immediately.',
      ],
    },
    links: {
      github: 'https://github.com/GAlbracht/WarehouseAnalysisExt',
      detailRoute: '/material-zone-analytics',
    },
  },
  {
    slug: 'amz-sp-api-pipeline',
    path: '/amz-category-analysis',
    title: 'Amazon SP-API Data Pipeline & Category Analytics',
    tagline:
      'SP-API and report exports cleaned and normalized into a curated dataset for category and product-level insight.',
    featured: true,
    order: 2,
    cardDescription:
      'A small data product built from a messy external API: SP-API and report exports cleaned with pandas, merged into a curated dataset, and turned into category-level insights.',
    sections: {
      problem:
        'Amazon SP-API and report exports arrive inconsistent — mismatched category taxonomies, missing fields, and mixed formats across export types — which makes it hard to trust any single export for category or product analysis.',
      dataSources:
        'Amazon SP-API pulls and bulk report exports covering product listings, attributes, and performance fields.',
      pipeline:
        'Python and pandas clean and normalize the raw exports: standardizing inconsistent category labels, backfilling or dropping incomplete records, and reconciling mixed date/numeric formats across sources. The cleaned tables are merged into a single curated dataset built for aggregation, not just storage.',
      tools: ['Python', 'pandas', 'Amazon SP-API', 'Excel'],
      outcome:
        'Produces a reliable, category-level view of product performance that supports assortment and inventory decisions, and outputs cleanly into Excel pivots or BI tools without another cleanup pass.',
      improvements: [
        'Separate raw, curated, and reporting layers instead of overwriting cleaned data in place.',
        'Add data-freshness checks so a stale or partial SP-API pull is caught before it reaches reporting.',
        'Add reconciliation and validation tests comparing category totals between raw and curated layers.',
        'Move the pipeline configuration (category mappings, field rules) into version control instead of inline script constants.',
      ],
    },
    links: {
      github: 'https://github.com/GAlbracht/Amazon-Category-Analysis',
      detailRoute: '/amz-category-analysis',
    },
  },
  {
    slug: 'amazon-commerce-reporting',
    path: '/sales-dashboard',
    title: 'Amazon Commerce Reporting Model & Power BI Dashboard',
    tagline:
      'SP-API data landed, transformed, and modeled into a Power BI semantic layer that stays in sync with the business.',
    featured: true,
    order: 3,
    cardDescription:
      'An automated reporting pipeline that replaces manual spreadsheet exports with a curated data model and a Power BI semantic layer refreshed on a schedule.',
    sections: {
      problem:
        'Sales reporting depended on manually stitched spreadsheet exports, which meant stale numbers, inconsistent calculations between reports, and no single source of truth for revenue, units, or conversion.',
      dataSources:
        'Amazon SP-API extracts covering shipped revenue, units, cost of goods sold, and conversion metrics.',
      pipeline:
        'Raw SP-API data lands in storage, then Python and SQL transformations clean and standardize it into a curated reporting model. Microsoft Fabric orchestrates the scheduled refresh from curated tables into the Power BI semantic model, keeping calculated measures consistent across every report and visual built on top of it.',
      tools: ['Amazon SP-API', 'Python', 'SQL', 'Microsoft Fabric', 'Power BI'],
      outcome:
        'Stakeholders get an always-current view of revenue, units, COGS, and conversion with period-over-period and category-level drill-downs, replacing manual spreadsheet reporting with a repeatable, scheduled pipeline.',
      improvements: [
        'Push transformations further upstream into the warehouse (warehouse-first) instead of doing cleanup in Python before load.',
        'Add freshness checks on the scheduled Fabric refresh so a failed or partial load doesn’t silently reach the dashboard.',
        'Add observability and error handling around each pipeline stage, not just the refresh schedule.',
        'Add reconciliation tests comparing semantic-model totals back to the curated reporting tables after each refresh.',
      ],
    },
    links: {
      detailRoute: '/sales-dashboard',
    },
  },
  {
    slug: 'earlier-analytics-work',
    path: '/earlier-analytics-work',
    title: 'Earlier Analytics & Measurement Work',
    tagline:
      'GA4, Google Tag Manager, and Search Console implementations that improved measurement and reporting visibility.',
    featured: false,
    order: 4,
    cardDescription:
      'Earlier measurement and analytics implementation work: GA4, Google Tag Manager, and Search Console setup across an ecommerce site and a community bank.',
    sections: {
      problem:
        'Two organizations had limited visibility into how visitors used their sites — traffic sources, on-site engagement, conversion behavior, and campaign performance were not being measured consistently.',
      dataSources:
        'Google Analytics 4, Google Tag Manager, and Google Search Console data across an ecommerce site and a community bank website.',
      pipeline:
        'Implemented GA4 and Google Tag Manager tracking, configured ecommerce and conversion events, resolved indexing and data-quality issues surfaced in Search Console, and built reporting to summarize traffic, engagement, and conversion trends.',
      tools: ['Google Analytics 4', 'Google Tag Manager', 'Google Search Console'],
      outcome:
        'Gave both organizations reliable, ongoing visibility into traffic, engagement, conversion behavior, and campaign performance where none had existed before.',
      improvements: [
        'Add automated data-quality checks on GA4/GTM event tracking instead of periodic manual review.',
        'Version-control tag and trigger configurations rather than managing them only inside the GTM UI.',
        'Build a lightweight reporting layer that pulls GA4/Search Console data on a schedule instead of relying on native dashboards.',
      ],
    },
    links: {
      detailRoute: '/earlier-analytics-work',
    },
  },
];

export default projects;
